'use client';

import {
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
} from 'react';
import {
  ArrowLeft,
  BriefcaseBusiness,
  Car,
  Check,
  Dumbbell,
  GraduationCap,
  HeartPulse,
  Home,
  PartyPopper,
  PawPrint,
  Scissors,
  Search,
  Shapes,
  Sparkles,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  MAX_OTHER_BUSINESS_TYPE_LENGTH,
  createBusinessClassificationSelection,
  getBusinessTypeDisplayName,
  searchBusinessTaxonomy,
  type BusinessCategoryOption,
  type BusinessClassificationAnalyticsSource,
  type BusinessClassificationSelection,
  type BusinessTypeOption,
} from '@/lib/business-taxonomy';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

export interface BusinessClassificationSelectorProps {
  analyticsSource: BusinessClassificationAnalyticsSource;
  categories: BusinessCategoryOption[];
  disabled?: boolean;
  errorMessage?: string | null;
  excludedTypeIds?: readonly string[];
  onChange: (value: BusinessClassificationSelection) => void;
  value: BusinessClassificationSelection | null;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  beauty: Scissors,
  belleza: Scissors,
  briefcase: BriefcaseBusiness,
  'briefcase-business': BriefcaseBusiness,
  car: Car,
  education: GraduationCap,
  events: PartyPopper,
  fitness: Dumbbell,
  'graduation-cap': GraduationCap,
  health: HeartPulse,
  'heart-pulse': HeartPulse,
  hogar: Home,
  home: Home,
  house: Home,
  mascotas: PawPrint,
  other: Shapes,
  'party-popper': PartyPopper,
  'paw-print': PawPrint,
  pets: PawPrint,
  salud: HeartPulse,
  services: BriefcaseBusiness,
  sparkles: Sparkles,
  wrench: Wrench,
};

function getCategoryIcon(icon: string | null): LucideIcon {
  if (!icon) return Shapes;
  return CATEGORY_ICONS[icon.trim().toLocaleLowerCase('es')] ?? Shapes;
}

function isTypeExcluded(
  typeId: string,
  excludedTypeIds: readonly string[]
): boolean {
  return excludedTypeIds.includes(typeId);
}

export function BusinessClassificationSelector({
  analyticsSource,
  categories,
  disabled = false,
  errorMessage,
  excludedTypeIds = [],
  onChange,
  value,
}: BusinessClassificationSelectorProps) {
  const searchInputId = useId();
  const searchResultsId = useId();
  const otherInputId = useId();
  const otherErrorId = useId();
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(
    value?.categoryId ?? null
  );
  const [query, setQuery] = useState('');
  const [activeResultIndex, setActiveResultIndex] = useState(-1);

  const activeCategory =
    categories.find((category) => category.id === activeCategoryId) ?? null;
  const activeCategoryCustomType =
    activeCategory?.types.find((type) => type.requiresCustomText) ?? null;
  const activeCategoryStandardTypes =
    activeCategory?.types.filter((type) => !type.requiresCustomText) ?? [];
  const searchResults = useMemo(
    () => searchBusinessTaxonomy(categories, query),
    [categories, query]
  );
  const hasQuery = query.trim().length > 0;
  const activeResult =
    activeResultIndex >= 0 ? searchResults[activeResultIndex] : null;

  useEffect(() => {
    if (!value?.categoryId) return;
    setActiveCategoryId(value.categoryId);
  }, [value?.categoryId]);

  useEffect(() => {
    if (!hasQuery) {
      setActiveResultIndex(-1);
      return;
    }

    const firstEnabledIndex = searchResults.findIndex(
      (result) => !isTypeExcluded(result.type.id, excludedTypeIds)
    );
    setActiveResultIndex(firstEnabledIndex);
  }, [excludedTypeIds, hasQuery, searchResults]);

  const chooseCategory = (category: BusinessCategoryOption) => {
    if (disabled) return;
    setActiveCategoryId(category.id);
    setQuery('');
    trackEvent('business_category_selected', {
      category_slug: category.slug,
      source: analyticsSource,
    });
  };

  const chooseType = (
    category: BusinessCategoryOption,
    type: BusinessTypeOption
  ) => {
    if (disabled || isTypeExcluded(type.id, excludedTypeIds)) return;
    const currentOtherText =
      value?.typeId === type.id ? value.otherBusinessTypeText : '';

    setActiveCategoryId(category.id);
    setQuery('');
    onChange(
      createBusinessClassificationSelection(
        category,
        type,
        currentOtherText
      )
    );
    trackEvent('business_type_selected', {
      business_type_slug: type.slug,
      category_slug: category.slug,
      source: analyticsSource,
    });
  };

  const moveActiveResult = (direction: 1 | -1) => {
    if (searchResults.length === 0) return;

    let nextIndex = activeResultIndex;
    for (let attempts = 0; attempts < searchResults.length; attempts += 1) {
      nextIndex =
        (nextIndex + direction + searchResults.length) % searchResults.length;
      if (
        !isTypeExcluded(searchResults[nextIndex].type.id, excludedTypeIds)
      ) {
        setActiveResultIndex(nextIndex);
        return;
      }
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActiveResult(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActiveResult(-1);
    } else if (event.key === 'Enter' && activeResult) {
      event.preventDefault();
      chooseType(activeResult.category, activeResult.type);
    } else if (event.key === 'Escape' && hasQuery) {
      event.preventDefault();
      setQuery('');
    }
  };

  const updateOtherBusinessTypeText = (otherBusinessTypeText: string) => {
    if (!value) return;
    onChange({
      ...value,
      otherBusinessTypeText: otherBusinessTypeText.slice(
        0,
        MAX_OTHER_BUSINESS_TYPE_LENGTH
      ),
    });
  };

  const otherTextMissing =
    value?.requiresCustomText === true &&
    value.otherBusinessTypeText.trim().length < 2 &&
    Boolean(errorMessage);

  return (
    <div className="min-w-0 space-y-5">
      <div className="space-y-2">
        <label
          htmlFor={searchInputId}
          className="block text-sm font-semibold text-text-primary"
        >
          Busca tu actividad
        </label>
        <div className="relative">
          <Search
            size={18}
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
          />
          <input
            id={searchInputId}
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-controls={hasQuery ? searchResultsId : undefined}
            aria-expanded={hasQuery}
            aria-activedescendant={
              activeResultIndex >= 0
                ? `${searchResultsId}-option-${activeResultIndex}`
                : undefined
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            disabled={disabled}
            placeholder="Ej. manicurista, psicología o plomería"
            className="h-12 w-full rounded-button border border-border-strong bg-bg-primary/70 pl-11 pr-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary/65 focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>
        {/* <p className="text-xs leading-5 text-text-secondary">
          Puedes buscar por el nombre o por una palabra relacionada.
        </p> */}
      </div>

      {hasQuery ? (
        <div
          id={searchResultsId}
          role="listbox"
          aria-label="Resultados de actividades"
          className="max-h-80 overflow-y-auto rounded-card border border-border bg-bg-primary/55 p-2 scrollbar-thin"
        >
          {searchResults.length > 0 ? (
            <div className="space-y-1">
              {searchResults.map((result, index) => {
                const isExcluded = isTypeExcluded(
                  result.type.id,
                  excludedTypeIds
                );
                const isActive = activeResultIndex === index;

                return (
                  <button
                    id={`${searchResultsId}-option-${index}`}
                    key={`${result.category.id}-${result.type.id}`}
                    type="button"
                    role="option"
                    aria-selected={value?.typeId === result.type.id}
                    disabled={disabled || isExcluded}
                    onClick={() => chooseType(result.category, result.type)}
                    className={cn(
                      'flex min-h-14 w-full items-center gap-3 rounded-button border px-3 py-3 text-left transition-colors',
                      value?.typeId === result.type.id
                        ? 'border-accent bg-accent/12'
                        : isActive
                          ? 'border-accent/60 bg-accent/10'
                          : 'border-transparent hover:border-border-strong hover:bg-bg-secondary/70',
                      isExcluded && 'cursor-not-allowed opacity-55'
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-heading text-sm font-semibold text-text-primary">
                        {result.type.name}
                      </span>
                      <span className="mt-0.5 block text-xs text-text-secondary">
                        {result.category.name}
                      </span>
                    </span>
                    {value?.typeId === result.type.id ? (
                      <Check
                        size={18}
                        aria-hidden="true"
                        className="shrink-0 text-accent"
                      />
                    ) : isExcluded ? (
                      <span className="shrink-0 text-xs font-semibold text-text-secondary">
                        Ya agregada
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div
              role="status"
              className="px-4 py-8 text-center text-sm text-text-secondary"
            >
              No encontramos una actividad con esa búsqueda.
            </div>
          )}
        </div>
      ) : activeCategory ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-heading text-lg font-semibold text-text-primary">
                ¿Qué opción describe mejor tu negocio?
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                {activeCategory.name}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => setActiveCategoryId(null)}
            >
              <ArrowLeft size={16} aria-hidden="true" />
              Volver a categorías
            </Button>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {activeCategoryStandardTypes.map((type) => {
              const isSelected = value?.typeId === type.id;
              const isExcluded = isTypeExcluded(type.id, excludedTypeIds);

              return (
                <button
                  key={type.id}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={disabled || isExcluded}
                  onClick={() => chooseType(activeCategory, type)}
                  className={cn(
                    'flex min-h-14 items-center gap-3 rounded-button border px-4 py-3 text-left transition-colors',
                    isSelected
                      ? 'border-accent bg-accent/12 text-text-primary'
                      : 'border-border-strong bg-bg-primary/45 text-text-primary hover:border-accent/70 hover:bg-accent/10',
                    isExcluded && 'cursor-not-allowed opacity-55'
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">
                      {type.name}
                    </span>
                    {isExcluded ? (
                      <span className="mt-0.5 block text-xs text-text-secondary">
                        Ya agregada
                      </span>
                    ) : null}
                  </span>
                  {isSelected ? (
                    <Check
                      size={18}
                      aria-hidden="true"
                      className="shrink-0 text-accent"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          {activeCategoryCustomType ? (
            <button
              type="button"
              aria-pressed={value?.typeId === activeCategoryCustomType.id}
              disabled={
                disabled ||
                isTypeExcluded(
                  activeCategoryCustomType.id,
                  excludedTypeIds
                )
              }
              onClick={() =>
                chooseType(activeCategory, activeCategoryCustomType)
              }
              className={cn(
                'flex min-h-12 w-full items-center justify-center gap-2 rounded-button border border-dashed px-4 py-3 text-sm font-semibold transition-colors',
                value?.typeId === activeCategoryCustomType.id
                  ? 'border-accent bg-accent/10 text-text-primary'
                  : 'border-border-strong bg-bg-primary/35 text-text-secondary hover:border-accent hover:text-accent',
                isTypeExcluded(
                  activeCategoryCustomType.id,
                  excludedTypeIds
                ) && 'cursor-not-allowed opacity-55'
              )}
            >
              <Shapes size={17} aria-hidden="true" />
              No encuentro mi actividad
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="font-heading text-lg font-semibold text-text-primary">
              Elige la categoría principal
            </p>
            {/* <p className="mt-1 text-sm leading-6 text-text-secondary">
              Selecciona la que más se parezca a tu negocio.
            </p> */}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {categories.map((category) => {
              const Icon = getCategoryIcon(category.icon);
              return (
                <button
                  key={category.id}
                  type="button"
                  aria-pressed={activeCategoryId === category.id}
                  disabled={disabled}
                  onClick={() => chooseCategory(category)}
                  className="group flex min-h-28 items-start gap-3 rounded-card border border-border-strong bg-bg-primary/45 p-4 text-left transition-colors hover:border-accent/70 hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-button border border-accent/25 bg-accent/10 text-accent">
                    <Icon size={21} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-sm font-semibold leading-5 text-text-primary">
                      {category.name}
                    </span>
                    {category.description ? (
                      <span className="mt-1.5 line-clamp-2 block text-xs leading-5 text-text-secondary">
                        {category.description}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value?.requiresCustomText ? (
        <div className="rounded-card border border-accent/30 bg-accent/10 p-4">
          <label
            htmlFor={otherInputId}
            className="block text-sm font-semibold text-text-primary"
          >
            Cuéntanos qué actividad realizas
          </label>
          <input
            id={otherInputId}
            type="text"
            value={value.otherBusinessTypeText}
            onChange={(event) =>
              updateOtherBusinessTypeText(event.target.value)
            }
            maxLength={MAX_OTHER_BUSINESS_TYPE_LENGTH}
            disabled={disabled}
            aria-invalid={otherTextMissing}
            aria-describedby={otherTextMissing ? otherErrorId : undefined}
            placeholder="Ej. Restauración de muebles"
            className="mt-2 h-12 w-full rounded-button border border-border-strong bg-bg-primary px-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary/65 focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <div className="mt-1.5 flex items-start justify-between gap-3 text-xs">
            {otherTextMissing ? (
              <p id={otherErrorId} role="alert" className="text-error">
                Escribe al menos 2 caracteres para continuar.
              </p>
            ) : (
              <span className="text-text-secondary">
                Este texto no crea una categoría pública nueva.
              </span>
            )}
            <span className="shrink-0 text-text-secondary">
              {value.otherBusinessTypeText.length}/
              {MAX_OTHER_BUSINESS_TYPE_LENGTH}
            </span>
          </div>
        </div>
      ) : null}

      {errorMessage && !otherTextMissing ? (
        <p role="alert" className="text-sm text-error">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}

export function BusinessClassificationSummary({
  label = 'Tu actividad principal',
  onChange,
  selection,
}: {
  label?: string;
  onChange?: () => void;
  selection: BusinessClassificationSelection;
}) {
  return (
    <div className="rounded-card border border-accent/30 bg-accent/10 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
            {label}
          </p>
          <p className="mt-2 break-words font-heading text-lg font-semibold text-text-primary">
            {getBusinessTypeDisplayName(selection)}
          </p>
          <p className="mt-1 break-words text-sm text-text-secondary">
            {selection.categoryName}
          </p>
        </div>
        {onChange ? (
          <Button type="button" size="sm" variant="ghost" onClick={onChange}>
            Cambiar
          </Button>
        ) : null}
      </div>
    </div>
  );
}
